import { useState } from 'react';
import { useSubscription } from './SubscriptionContext';

const SubscriptionManagementModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { subscription, createPortalSession } = useSubscription();

  const handleManageSubscription = async () => {
    setLoading(true);
    setError('');

    try {
      const portalUrl = await createPortalSession(`${window.location.origin}/`);
      window.location.href = portalUrl;
    } catch (err) {
      setError('Failed to open subscription management. Please try again.');
      console.error('Portal error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed h-screen w-screen inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-xl border border-purple-100/30">
        <div className="text-center space-y-3">
          {/* Icon */}
          <div className="w-10 h-10 mx-auto text-purple-500">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <h3 className="text-2xl font-medium text-gray-900 font-fraunces">
              Your PRO Subscription
            </h3>
            
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 font-space">Status:</span>
                <span className="text-sm text-green-600 font-space font-semibold">Active</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 font-space">Plan:</span>
                <span className="text-sm text-gray-900 font-space">MoodShelf PRO</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 font-space">Price:</span>
                <span className="text-sm text-gray-900 font-space">$5.00/month</span>
              </div>
              
              {subscription?.currentPeriodEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 font-space">Next billing:</span>
                  <span className="text-sm text-gray-900 font-space">
                    {formatDate(subscription.currentPeriodEnd)}
                  </span>
                </div>
              )}
              
              {subscription?.cancelAtPeriodEnd && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-orange-700 font-space">
                      Cancels at period end
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="bg-blue-50 rounded-xl p-4 space-y-2">
              <h4 className="font-semibold text-blue-900 font-space">Your PRO Benefits</h4>
              <ul className="text-sm text-blue-800 font-space space-y-1 text-left">
                <li>• Unlimited similar book recommendations</li>
                <li>• Unlimited mood-based recommendations</li>
                <li>• No daily limits or restrictions</li>
                <li>• Priority support</li>
                <li>• Early access to new features</li>
              </ul>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleManageSubscription}
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-space disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Manage Subscription'}
            </button>
            
            <button
              onClick={onClose}
              className="w-full px-6 py-2 text-gray-500 text-sm hover:text-gray-700 transition-colors font-space"
            >
              Close
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <p className="text-red-600 text-sm font-space">{error}</p>
          )}

          {/* Info Note */}
          <p className="text-xs text-gray-500 font-space">
            Manage your subscription, update payment methods, or cancel anytime through Stripe's secure portal.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagementModal; 